// Modules
import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ErrorResponse } from 'apollo-link-error'

// Applications
import { useApp } from 'context'
import { useActiveCIPOrphanHook, useAnalytics, useLoginHook } from 'hooks'
import { ContactType, StatusMessage } from 'typings'
import { OutputErrorCode, OutputErrorMsg, Analytics, ROUTES_PATH } from 'constants/index'

import { ResendTokenType } from 'typings/account'

// Components
import { ValidateTokenInitial } from 'components/Presenters/ValidateDestinationToken'
import { ValidateToken } from 'components/Presenters/ValidateToken'
import { TemplateContainer, Header } from 'components'

export const MDMCIPValidateToken: React.VFC = () => {
    const history = useHistory()

    const { openToast } = useApp()
    const { verifyUser, signOut } = useLoginHook()
    const {
        cpf,
        biologicalSex,
        password,
        hasMounted,
        isSubmitting,
        contact,
        confirmToken,
        resendToken,
        resetAccountState,
    } = useActiveCIPOrphanHook()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [fieldState, setFieldState] = useState<StatusMessage>({ status: '', message: '' })

    const shouldRedirect = !cpf || !biologicalSex || !password

    // evento funil
    useAnalytics(Analytics.ActivateAccountCipOrphan.MdmAccountToken, !shouldRedirect)

    const SUBTITLE_CONTENT: string = useMemo(() => {
        let subtitle = 'Um código de segurança foi enviado para o [CONTACT_TYPE] <span>[CONTACT_VALUE]</span>.'
        subtitle = subtitle.replace('[CONTACT_VALUE]', contact?.value || '')
        subtitle = subtitle.replace('[CONTACT_TYPE]', contact?.type === 'EMAIL' ? 'e-mail' : 'celular')

        return subtitle
    }, [contact])

    if (!hasMounted) {
        return null
    }

    if (shouldRedirect) {
        history.push(ROUTES_PATH.SIGN_UP_REGISTER_ACTIVATION_CPF)
        return null
    }

    const inicialValues = {
        token: [null, null, null, null, null, null],
    }

    const isDestinationMobile = contact?.type !== 'EMAIL'
    const isDestinationEmail = contact?.contactType === 'EMAIL'

    const handleSubmit = async (values: ValidateTokenInitial) => {
        setFieldState({ status: 'loading', message: 'Validando código' })
        const { token: rawToken } = values
        const token = rawToken.join('')

        sessionStorage.setItem('@LiviaAuth:PhoneToken', token)
        try {
            const successfull = await confirmToken(token)

            setFieldState({ status: 'success', message: 'Código validado' })

            if (successfull) {
                await verifyUser()
                signOut()
                history.push(ROUTES_PATH.SIGN_IN, {
                    analytics: Analytics.ActivateAccountCipOrphan.RegisterExamesMDMSignUp,
                })
                resetAccountState()
            } else {
                setTimeout(() => {
                    history.push(ROUTES_PATH.SIGN_IN, { status: 'success', message: 'Cadastro realizado com sucesso!' })
                }, 3600)
            }
        } catch (err) {
            const e: ErrorResponse = err
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                const codeError = e.graphQLErrors[0].extensions?.code
                const isError = codeError === OutputErrorCode.INVALIDTOKEN || codeError === OutputErrorCode.EXPIREDTOKEN
                const isInvalidToken = codeError === OutputErrorCode.INVALIDTOKEN

                if (isError) {
                    setFieldState({
                        status: 'error',
                        message: isInvalidToken ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })
                    return
                }
            }
            setFieldState({ status: '', message: '' })
            openToast('Ocorreu um erro')
        }
    }

    const handleResendToken = async () => {
        try {
            setLoading(true)
            const patientLogin: ResendTokenType = {
                contactId: contact?.id,
                cpf: cpf,
                password: password,
                destination: isDestinationEmail ? ContactType.EMAIL : ContactType.SMS,
            }
            await resendToken(patientLogin)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            openToast('Ocorreu um erro')
        }
    }

    return (
        <TemplateContainer>
            <Header subtitle={SUBTITLE_CONTENT} title="Insira o código" />
            <ValidateToken
                fieldState={fieldState}
                handleResendToken={handleResendToken}
                handleSubmit={handleSubmit}
                initialValues={inicialValues}
                isDestinationMobile={isDestinationMobile}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
            />
        </TemplateContainer>
    )
}
